import { Check, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";

interface CommentInputProps {
  commentValue: string;
  hasSavedComment: boolean;
  showSave?: boolean;
  onCommentChange: (value: string) => void;
  onSave: () => void;
  onDelete?: () => void;
  onClose: () => void;
}

function CommentInput({
  commentValue,
  hasSavedComment,
  showSave = true,
  onCommentChange,
  onSave,
  onDelete,
  onClose,
}: CommentInputProps) {
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const [isComposing, setIsComposing] = useState(false);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleSave = () => {
    onSave();
    onClose();
  };

  return (
    <div className="flex min-h-[58px] items-start gap-1 rounded-sm bg-light-muted px-2 py-1.5">
      <label htmlFor="wanted-comment-input" className="sr-only">
        사유 메모
      </label>
      <textarea
        ref={inputRef}
        id="wanted-comment-input"
        aria-describedby="wanted-comment-input-help"
        rows={2}
        className="h-11 min-w-0 flex-1 resize-none border-0 bg-transparent px-1 py-0.5 text-xs leading-5 text-text-main2 outline-none placeholder:text-text-sub focus-visible:ring-0"
        placeholder="사유를 작성하세요"
        value={commentValue}
        onChange={(event) => onCommentChange(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === "Escape") {
            event.preventDefault();
            onClose();
            return;
          }

          if (
            event.key === "Enter" &&
            (event.ctrlKey || event.metaKey) &&
            !event.nativeEvent.isComposing &&
            !isComposing
          ) {
            event.preventDefault();
            handleSave();
          }
        }}
        onCompositionStart={() => setIsComposing(true)}
        onCompositionEnd={() => setIsComposing(false)}
        maxLength={100}
      />
      <span id="wanted-comment-input-help" className="sr-only">
        Ctrl 또는 Command와 Enter로 저장하고 Escape로 닫습니다.
      </span>
      {showSave && (
        <div className="flex shrink-0 items-end gap-0.5 self-end">
          {hasSavedComment && onDelete && (
            <button
              type="button"
              aria-label="사유 삭제"
              title="사유 삭제"
              className="flex size-6 items-center justify-center rounded-sm text-text-sub transition-colors hover:bg-destructive/10 hover:text-destructive focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-text-sub/40 focus-visible:ring-offset-0"
              onClick={() => {
                onDelete();
                onClose();
              }}
            >
              <X aria-hidden="true" className="size-3.5" />
            </button>
          )}
          <button
            type="button"
            aria-label="사유 저장"
            title="사유 저장"
            className="flex size-6 items-center justify-center rounded-sm text-sig transition-colors hover:bg-sig/10 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-text-sub/40 focus-visible:ring-offset-0"
            onClick={handleSave}
          >
            <Check aria-hidden="true" className="size-3.5" />
          </button>
        </div>
      )}
    </div>
  );
}

export default CommentInput;
